import { useMemo, useCallback } from 'react';
import { useCurrentUser } from '../contexts/AuthContext';
import { useTenant } from '../contexts/TenantContext';
import { Capability } from '../types';
import { ROLE_PRIORITY, ALL_CAPABILITIES, CAPABILITY_ALIASES } from '../constants';

export const normalizeRoleString = (r: any): string => {
  if (!r) return '';
  let str = r.toString().toUpperCase().trim();
  if (str.startsWith('ROLE_')) {
    str = str.substring(5);
  }
  if (str === 'SUPERADMIN' || str === 'SUPERUSER' || str === 'SUPER_USER') return 'SUPER_ADMIN';
  if (str === 'SYSTEMADMIN') return 'SYSTEM_ADMIN';
  if (str === 'OPERATIONSADMIN') return 'OPERATIONS_ADMIN';
  if (str === 'SUPPORTADMIN') return 'SUPPORT_ADMIN';
  return str;
};

export const usePermissions = () => {
  const { memberships = [], user, isGodMode } = useCurrentUser();
  const { node, lineage } = useTenant();

  // Helper to extract system-level role from user metadata / Spring Boot profile / God Mode
  const userSystemRole = useMemo(() => {
    const isGod = isGodMode || (import.meta.env.DEV && typeof localStorage !== 'undefined' && localStorage.getItem('GOD_MODE_ENABLED') === 'true');
    if (isGod) return 'SYSTEM_ADMIN';

    // Direct super admin boolean flags from Clerk or Spring Boot
    const isDirectSuper = Boolean(
      user?.isSuperAdmin ||
      user?.superAdmin ||
      user?.isSuperUser ||
      user?.superUser ||
      user?.publicMetadata?.isSuperAdmin ||
      user?.unsafeMetadata?.isSuperAdmin ||
      user?.publicMetadata?.superAdmin ||
      user?.unsafeMetadata?.superAdmin ||
      user?.publicMetadata?.isSuperUser ||
      user?.unsafeMetadata?.isSuperUser ||
      user?.publicMetadata?.superUser ||
      user?.unsafeMetadata?.superUser
    );
    if (isDirectSuper) return 'SUPER_ADMIN';

    // Direct system admin boolean flags
    const isDirectSys = Boolean(
      user?.isSystemAdmin ||
      user?.systemAdmin ||
      user?.publicMetadata?.isSystemAdmin ||
      user?.unsafeMetadata?.isSystemAdmin ||
      user?.publicMetadata?.systemAdmin ||
      user?.unsafeMetadata?.systemAdmin
    );
    if (isDirectSys) return 'SYSTEM_ADMIN';

    // Check Spring Security authorities or roles array
    const authList = user?.authorities || user?.roles || [];
    if (Array.isArray(authList)) {
      for (const item of authList) {
        const raw = typeof item === 'string' ? item : (item?.authority || item?.name || item?.role || '');
        const norm = normalizeRoleString(raw);
        if (['SUPER_ADMIN', 'SYSTEM_ADMIN', 'OPERATIONS_ADMIN', 'SUPPORT_ADMIN'].includes(norm)) {
          return norm;
        }
      }
    }

    const rawRole = normalizeRoleString(
      user?.systemRole ||
      user?.publicMetadata?.role ||
      user?.unsafeMetadata?.role ||
      user?.role ||
      ''
    );

    if (['SUPER_ADMIN', 'SYSTEM_ADMIN', 'OPERATIONS_ADMIN', 'SUPPORT_ADMIN'].includes(rawRole)) {
      return rawRole;
    }

    // Check user's tenant memberships for system-level administrative assignments
    for (const m of memberships) {
      const mNorm = normalizeRoleString(m.role);
      if (mNorm === 'SUPER_ADMIN') return 'SUPER_ADMIN';
      if (mNorm === 'SYSTEM_ADMIN') return 'SYSTEM_ADMIN';
      if (mNorm === 'OPERATIONS_ADMIN') return 'OPERATIONS_ADMIN';
      if (mNorm === 'SUPPORT_ADMIN') return 'SUPPORT_ADMIN';
    }

    return null;
  }, [user, memberships, isGodMode]);

  // 1. Determine effective role (highest single role in lineage or global system role)
  const role = useMemo(() => {
    if (userSystemRole) return userSystemRole;

    if (!node || node.id === 'platform-root') {
      if (memberships.length > 0) {
        let highest = 'MEMBER';
        let maxPri = -1;
        memberships.forEach(m => {
          const normRole = normalizeRoleString(m.role);
          const pri = ROLE_PRIORITY[normRole] || ROLE_PRIORITY[m.role?.toUpperCase()] || 0;
          if (pri > maxPri) {
            maxPri = pri;
            highest = normRole || m.role;
          }
        });
        return highest;
      }
      return user ? (normalizeRoleString(user?.publicMetadata?.role || user?.role) || 'MEMBER') : 'GUEST';
    }
    
    // Current node ID + all its ancestors
    const nodeIdsToCheck = [node.id, ...(node.lineageIds || [])];
    const relevantMemberships = memberships.filter(m => nodeIdsToCheck.includes(m.id));

    if (relevantMemberships.length === 0) {
      if (memberships.length > 0) {
        let highest = 'MEMBER';
        let maxPri = -1;
        memberships.forEach(m => {
          const normRole = normalizeRoleString(m.role);
          const pri = ROLE_PRIORITY[normRole] || 0;
          if (pri > maxPri) {
            maxPri = pri;
            highest = normRole || m.role;
          }
        });
        return highest;
      }
      return user ? (normalizeRoleString(user?.publicMetadata?.role || user?.role) || 'MEMBER') : 'GUEST';
    }

    let highestRole = 'MEMBER';
    let highestPriority = -1;

    relevantMemberships.forEach(m => {
      const normRole = normalizeRoleString(m.role);
      let priority = ROLE_PRIORITY[normRole] || ROLE_PRIORITY[m.role?.toUpperCase()] || 0;
      
      // Attempt to resolve custom dynamic priority from the node
      const targetNode = m.id === node.id ? node : lineage.find(n => n.id === m.id);
      if (targetNode && targetNode.config?.roleDefinitions) {
        const customDef = targetNode.config.roleDefinitions.find(
          d => normalizeRoleString(d.name) === normRole
        );
        if (customDef && typeof customDef.priority === 'number') {
          priority = customDef.priority;
        }
      }

      if (priority > highestPriority) {
        highestPriority = priority;
        highestRole = normRole || m.role;
      }
    });

    return highestRole;
  }, [userSystemRole, user, memberships, node, lineage]);

  const normalizedRole = useMemo(() => normalizeRoleString(role), [role]);

  const isGlobalAdmin = useMemo(() => {
    return Boolean(
      userSystemRole ||
      ['SYSTEM_ADMIN', 'SUPER_ADMIN', 'OPERATIONS_ADMIN', 'SUPPORT_ADMIN'].includes(normalizedRole)
    );
  }, [userSystemRole, normalizedRole]);

  const isAdminRole = useMemo(() => {
    return Boolean(
      isGlobalAdmin ||
      ['ADMIN', 'OWNER', 'PRESIDENT', 'SUPPORT', 'MANAGER', 'DIRECTOR'].includes(normalizedRole)
    );
  }, [isGlobalAdmin, normalizedRole]);

  // 2. Resolve all capabilities across lineage
  const capabilities = useMemo((): Capability[] => {
    if (isGlobalAdmin) {
      return [...ALL_CAPABILITIES] as Capability[];
    }
    if (!node || node.id === 'platform-root') {
      // Gather all capabilities across all memberships
      const allCaps = new Set<Capability>();
      memberships.forEach(m => {
        const mNorm = normalizeRoleString(m.role);
        if (Array.isArray(m.capabilities)) {
          m.capabilities.forEach(c => {
            allCaps.add(c);
            const aliases = CAPABILITY_ALIASES[c] || [];
            aliases.forEach(a => allCaps.add(a as Capability));
          });
        }
        if (['ADMIN', 'OWNER', 'PRESIDENT', 'SUPPORT', 'SYSTEM_ADMIN', 'SUPER_ADMIN', 'OPERATIONS_ADMIN', 'SUPPORT_ADMIN'].includes(mNorm)) {
          ALL_CAPABILITIES.forEach(c => allCaps.add(c));
        }
      });
      return Array.from(allCaps);
    }

    // All nodes in the hierarchy (current + lineage parents)
    const allRelevantNodeIds = [node.id, ...(node.lineageIds || [])];
    const caps = new Set<Capability>();

    // Directly gather capabilities resolved by the backend for relevant memberships
    memberships.forEach(m => {
      const mNorm = normalizeRoleString(m.role);
      if (allRelevantNodeIds.includes(m.id)) {
        if (Array.isArray(m.capabilities)) {
          m.capabilities.forEach(c => {
            caps.add(c);
            const aliases = CAPABILITY_ALIASES[c] || [];
            aliases.forEach(a => caps.add(a as Capability));
          });
        }

        // Also check dynamic role definitions on node config
        const targetNode = m.id === node.id ? node : lineage.find(n => n.id === m.id);
        if (targetNode?.config?.roleDefinitions) {
          const roleDef = targetNode.config.roleDefinitions.find(
            d => normalizeRoleString(d.name) === mNorm
          );
          if (roleDef && Array.isArray(roleDef.capabilities)) {
            roleDef.capabilities.forEach(c => {
              caps.add(c);
              const aliases = CAPABILITY_ALIASES[c] || [];
              aliases.forEach(a => caps.add(a as Capability));
            });
          }
        }

        // Hardcoded fallback for global administrative roles (safety gate)
        if (['ADMIN', 'OWNER', 'PRESIDENT', 'SUPPORT', 'SYSTEM_ADMIN', 'SUPER_ADMIN', 'OPERATIONS_ADMIN', 'SUPPORT_ADMIN'].includes(mNorm)) {
          ALL_CAPABILITIES.forEach(c => caps.add(c));
        }
      }
    });

    return Array.from(caps);
  }, [isGlobalAdmin, memberships, node, lineage]);

  const can = useCallback((action: Capability | string) => {
    if (isGlobalAdmin || ['ADMIN', 'OWNER', 'PRESIDENT', 'SUPER_ADMIN', 'SYSTEM_ADMIN'].includes(normalizedRole)) {
      return true;
    }
    if (capabilities.includes(action as Capability)) return true;
    const aliases = CAPABILITY_ALIASES[action] || [];
    return aliases.some(a => capabilities.includes(a as Capability));
  }, [isGlobalAdmin, capabilities, normalizedRole]);

  return useMemo(() => {
    const isSuper = normalizedRole === 'SUPER_ADMIN' ||
                    userSystemRole === 'SUPER_ADMIN' ||
                    normalizedRole === 'SYSTEM_ADMIN' ||
                    userSystemRole === 'SYSTEM_ADMIN' ||
                    Boolean(
                      user?.isSuperAdmin ||
                      user?.superAdmin ||
                      user?.isSuperUser ||
                      user?.superUser ||
                      user?.publicMetadata?.isSuperAdmin ||
                      user?.unsafeMetadata?.isSuperAdmin ||
                      user?.publicMetadata?.superAdmin ||
                      user?.unsafeMetadata?.superAdmin ||
                      user?.publicMetadata?.isSuperUser ||
                      user?.unsafeMetadata?.isSuperUser ||
                      user?.publicMetadata?.superUser ||
                      user?.unsafeMetadata?.superUser
                    );

    const isSys = isSuper || isGlobalAdmin || normalizedRole === 'SYSTEM_ADMIN' || userSystemRole === 'SYSTEM_ADMIN';
    
    const isLocal = isGlobalAdmin || isSys || isSuper || (node ? memberships.some(m => {
      const mNorm = normalizeRoleString(m.role);
      return m.id === node.id && ['ADMIN', 'OWNER', 'PRESIDENT', 'SUPPORT', 'SYSTEM_ADMIN', 'SUPER_ADMIN', 'OPERATIONS_ADMIN', 'SUPPORT_ADMIN'].includes(mNorm);
    }) : false);

    const isInherited = isGlobalAdmin || isSys || isSuper || (node ? (
      !memberships.some(m => m.id === node.id && ['ADMIN', 'OWNER', 'PRESIDENT', 'SUPPORT', 'SYSTEM_ADMIN', 'SUPER_ADMIN', 'OPERATIONS_ADMIN', 'SUPPORT_ADMIN'].includes(normalizeRoleString(m.role))) && 
      memberships.some(m => (node.lineageIds || []).includes(m.id) && ['ADMIN', 'OWNER', 'PRESIDENT', 'SUPPORT', 'SYSTEM_ADMIN', 'SUPER_ADMIN', 'OPERATIONS_ADMIN', 'SUPPORT_ADMIN'].includes(normalizeRoleString(m.role)))
    ) : false);

    return {
      role: normalizedRole || role,
      capabilities,
      can,
      isAdmin: isAdminRole || isSys || isSuper,
      isAtLeastModerator: isAdminRole || isSys || isSuper || normalizedRole === 'MODERATOR',
      isLocalAdmin: isLocal,
      isInheritedAdmin: isInherited,
      isSystemAdmin: isSys,
      isSuperAdmin: isSuper,
      isSupportAdmin: isSuper || normalizedRole === 'SUPPORT_ADMIN' || userSystemRole === 'SUPPORT_ADMIN',
      isOperationsAdmin: isSuper || normalizedRole === 'OPERATIONS_ADMIN' || userSystemRole === 'OPERATIONS_ADMIN'
    };
  }, [normalizedRole, role, capabilities, can, isAdminRole, isGlobalAdmin, userSystemRole, user, memberships, node]);
};
